import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Sparkles } from "lucide-react";
import { useUser } from "../utils/useUser";

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}

export default function LoginModal({ open, onClose }: LoginModalProps) {
  const { loginWithGoogle, devLogin, error: loginError } = useUser();
  const [devEmail, setDevEmail] = useState("musician@chordmaster.local");
  const [devName, setDevName] = useState("Guitarist");
  const [loadingDev, setLoadingDev] = useState(false);
  const [devError, setDevError] = useState<string | null>(null);

  const showDevLogin =
    process.env.NODE_ENV === "development" ||
    ["localhost", "127.0.0.1"].includes(window.location.hostname) ||
    process.env.REACT_APP_SHOW_DEV_LOGIN === "true";

  const handleDevSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoadingDev(true);
      setDevError(null);
      await devLogin(devEmail, devName);
      onClose();
    } catch (err: any) {
      setDevError(err.message || "Login failed");
    } finally {
      setLoadingDev(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            p: 1,
            boxShadow: 24,
          },
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
                flexShrink: 0,
              }}
            >
              <img
                src="/logo192.png"
                alt="ChordMaster Logo"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Sign in to ChordMaster
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sync chords, personal notes, and Kanban boards
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}
        <Box display="flex" flexDirection="column" gap={2.5}>
          {/* Primary Google Login Button */}
          <Button
            variant="outlined"
            fullWidth
            onClick={loginWithGoogle}
            sx={{
              py: 1.2,
              px: 2,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              borderColor: "#cbd5e1",
              color: "#1e293b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              "&:hover": {
                bgcolor: "#f8fafc",
                borderColor: "#94a3b8",
              },
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
            <span>Sign in with Google</span>
          </Button>

          {showDevLogin && (
            <>
              <Divider sx={{ my: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  OR DEVELOPER LOGIN
                </Typography>
              </Divider>

              <Box component="form" onSubmit={handleDevSubmit} display="flex" flexDirection="column" gap={1.5}>
                <TextField
                  size="small"
                  label="Email"
                  fullWidth
                  type="email"
                  value={devEmail}
                  onChange={(e) => setDevEmail(e.target.value)}
                  required
                />
                <TextField
                  size="small"
                  label="Display Name"
                  fullWidth
                  value={devName}
                  onChange={(e) => setDevName(e.target.value)}
                />

                {devError && (
                  <Alert severity="error" sx={{ py: 0.5, borderRadius: 2 }}>
                    {devError}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loadingDev || !devEmail.trim()}
                  startIcon={loadingDev ? <CircularProgress size={16} color="inherit" /> : <Sparkles size={16} />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                  }}
                >
                  {loadingDev ? "Signing in..." : "Dev Login"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
